import {
  DocumentExcelPane,
  DocumentPdfPane,
  DocumentViewer,
  type DocumentPaneRenderProps,
  type DocumentViewerHighlight,
  type DocumentViewerLayout,
} from '@aecom-ai-engineering/react-components';
import { useGetBlobQuery, useProjectId, useReadUrl } from '@consigli/hooks';
import { Document, useConvertFindingToDocument } from '@consigli/types';
import { type FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toExcelHighlight, toHighlights } from '@/features/document-viewer/excel-highlight';
import { useViewer } from '@/features/document-viewer/use-viewer';
import { useViewerContext } from '@/features/document-viewer/use-viewer-context';
import { useFindingsContextSafe } from '@/pages/insight/findings/findings-context';
import { isExcelDocumentType } from '@/util/document-check';

import { isDocumentDoubleViewerCapable } from './container';
import { FindingsController } from './controller';

interface DocumentViewerDrawerProps {
  itemsOnPage: number;
}

// Bid-review "double" findings compare a bid document (primary) against the
// tender/reference document (secondary). The reference has no URL on the
// finding, so resolve it from its blob; the primary keeps the finding's own
// signed URL.
const ComparisonDocumentViewer: FC<{
  document: Document;
  primarySrc: string;
  primaryIsExcel: boolean;
  title: string;
  initialPage: number;
  primaryHighlights?: DocumentViewerHighlight[];
  itemsOnPage: number;
  previousDisabled: boolean;
  nextDisabled: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onClose: () => void;
}> = ({
  document,
  primarySrc,
  primaryIsExcel,
  title,
  initialPage,
  primaryHighlights,
  itemsOnPage,
  previousDisabled,
  nextDisabled,
  onPrevious,
  onNext,
  onClose,
}) => {
  const projectId = useProjectId();
  const { readUrl: secondaryReadUrl } = useReadUrl(document, true);
  const { data: refBlob } = useGetBlobQuery({
    projectId,
    packageId: document.refDocumentPackageId || '',
    blobId: document.refBlobId || '',
  });

  const refFileName = document.refFileName ?? refBlob?.name;
  const refIsExcel = !!refFileName && isExcelDocumentType(refFileName);

  const secondarySrc = refIsExcel ? (refBlob?.downloadUrl ?? secondaryReadUrl) : secondaryReadUrl;

  const secondaryHighlights = useMemo(
    () => toHighlights(document.refFindingCoordinates),
    [document.refFindingCoordinates],
  );
  const primaryExcelHighlight = useMemo(
    () => (primaryIsExcel ? toExcelHighlight(document.findingCellCoordinates) : undefined),
    [primaryIsExcel, document.findingCellCoordinates],
  );
  const secondaryExcelHighlight = useMemo(
    () => (refIsExcel ? toExcelHighlight(document.refFindingCellCoordinates) : undefined),
    [refIsExcel, document.refFindingCellCoordinates],
  );
  const secondaryInitialPage = refIsExcel ? undefined : (document.refPageNumber ?? 0) + 1;

  const renderers = useMemo(
    () => ({
      excel: (props: DocumentPaneRenderProps) => (
        <DocumentExcelPane
          active={props.active}
          caption={props.caption}
          fileName=""
          highlight={props.src === primarySrc ? primaryExcelHighlight : secondaryExcelHighlight}
          searchable
          src={props.src}
        />
      ),
      pdf: (props: DocumentPaneRenderProps) => (
        <DocumentPdfPane
          active={props.active}
          caption={props.caption}
          onSidebarContextChange={props.onSidebarContextChange}
          src={props.src}
          initialPage={
            props.src === primarySrc
              ? primaryIsExcel
                ? undefined
                : initialPage
              : secondaryInitialPage
          }
          highlights={
            props.src === primarySrc
              ? primaryIsExcel
                ? undefined
                : primaryHighlights
              : refIsExcel
                ? undefined
                : secondaryHighlights
          }
          continuousScroll
        />
      ),
    }),
    [
      primarySrc,
      primaryIsExcel,
      primaryExcelHighlight,
      secondaryExcelHighlight,
      initialPage,
      secondaryInitialPage,
      primaryHighlights,
      refIsExcel,
      secondaryHighlights,
    ],
  );

  // The drawer opens stacked (bid over tender) so both fit the narrow pane;
  // maximizing to full screen switches to side-by-side where there's room. The
  // manual layout toggle still overrides within the current size.
  const maximizedRef = useRef(false);
  const [layout, setLayout] = useState<DocumentViewerLayout>('stacked');

  const handleExpand = useCallback(() => {
    maximizedRef.current = !maximizedRef.current;
    setLayout(maximizedRef.current ? 'side-by-side' : 'stacked');
  }, []);

  return (
    <DocumentViewer
      open
      onOpenChange={(details) => {
        if (!details.open) onClose();
      }}
      src={primarySrc}
      fileType={primaryIsExcel ? 'excel' : undefined}
      fileName={document.name}
      secondarySrc={secondarySrc}
      secondaryFileType={refIsExcel ? 'excel' : undefined}
      secondaryFileName={refFileName}
      title={title}
      primaryTitle={document.name}
      secondaryTitle={refFileName}
      layout={layout}
      onLayoutChange={setLayout}
      onExpand={handleExpand}
      showSidebar
      showNavigationActions
      previousDisabled={previousDisabled}
      nextDisabled={nextDisabled}
      onPrevious={onPrevious}
      onNext={onNext}
      renderers={renderers}
    >
      <FindingsController itemsOnPage={itemsOnPage} variant="sidebar" />
    </DocumentViewer>
  );
};

export const DocumentViewerDrawer: FC<DocumentViewerDrawerProps> = ({ itemsOnPage }) => {
  const { t } = useTranslation();
  const { document, setDocument } = useViewerContext();
  const { close } = useViewer();
  const findingsContext = useFindingsContextSafe();
  const convertFindingToDocument = useConvertFindingToDocument(t);

  const highlights = useMemo(
    () => toHighlights(document?.findingCoordinates),
    [document?.findingCoordinates],
  );

  const navigate = useCallback(
    (direction: 'next' | 'previous') => {
      if (!findingsContext) return;
      const finding =
        direction === 'next'
          ? findingsContext.getNextFinding()
          : findingsContext.getPreviousFinding();
      if (!finding) return;
      findingsContext.setCurrentFinding(finding);
      setDocument(convertFindingToDocument(finding));
    },
    [findingsContext, setDocument, convertFindingToDocument],
  );

  const isExcel = !!document && isExcelDocumentType(document.name);

  const excelHighlight = useMemo(
    () => (isExcel && document ? toExcelHighlight(document.findingCellCoordinates) : undefined),
    [isExcel, document],
  );

  const initialPage = document ? (document.pageNumber ?? 0) + 1 : 1;

  const singleRenderers = useMemo(
    () => ({
      excel: (props: DocumentPaneRenderProps) => (
        <DocumentExcelPane
          active={props.active}
          caption={props.caption}
          fileName=""
          highlight={excelHighlight}
          searchable
          src={props.src}
        />
      ),
      pdf: (props: DocumentPaneRenderProps) => (
        <DocumentPdfPane
          active={props.active}
          caption={props.caption}
          onSidebarContextChange={props.onSidebarContextChange}
          src={props.src}
          initialPage={isExcel ? undefined : initialPage}
          highlights={isExcel ? undefined : highlights}
          continuousScroll={!isExcel}
          searchable={!isExcel}
        />
      ),
    }),
    [excelHighlight, isExcel, initialPage, highlights],
  );

  if (!document || !document.fileUrl) return null;

  const previousDisabled = !findingsContext?.getPreviousFinding();
  const nextDisabled = !findingsContext?.getNextFinding();
  const title = document.name;
  const primarySrc = isExcel ? document.downloadUrl || document.fileUrl : document.fileUrl;

  if (isDocumentDoubleViewerCapable(document)) {
    return (
      <ComparisonDocumentViewer
        document={document}
        primarySrc={primarySrc}
        primaryIsExcel={isExcel}
        title={title}
        initialPage={initialPage}
        primaryHighlights={highlights}
        itemsOnPage={itemsOnPage}
        previousDisabled={previousDisabled}
        nextDisabled={nextDisabled}
        onPrevious={() => navigate('previous')}
        onNext={() => navigate('next')}
        onClose={close}
      />
    );
  }

  return (
    <DocumentViewer
      open
      onOpenChange={(details) => {
        if (!details.open) close();
      }}
      src={primarySrc}
      fileType={isExcel ? 'excel' : undefined}
      fileName={document.name}
      title={title}
      showSidebar
      showNavigationActions
      previousDisabled={previousDisabled}
      nextDisabled={nextDisabled}
      onPrevious={() => navigate('previous')}
      onNext={() => navigate('next')}
      renderers={singleRenderers}
    >
      <FindingsController itemsOnPage={itemsOnPage} variant="sidebar" />
    </DocumentViewer>
  );
};
